/**
 * Tag — Colored label for categorization, status, or metadata.
 *
 * Tag Component Guidelines
 * - Choose `variant` based on semantic meaning: `success` for positive states,
 *   `danger` for errors or destructive labels, `info` for informational, `neutral`
 *   for default/uncolored, `beta` for experimental features, `v2` for new design
 *   system indicators.
 * - Use `size="sm"` (16px) for inline annotations or dense lists; `size="base"`
 *   (24px) for standalone labels in cards or headers.
 * - Tag is non-interactive by default. Use the `render` prop to make it interactive
 *   (e.g. render as a `<button>` for removable filter chips).
 * - For small, icon-only indicators without semantic color, use Badge instead.
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";
import type { VariantProps } from "class-variance-authority";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";

const tagVariants = cva(
  "border border-0.5 transition-all inline-flex items-center justify-center text-center w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-colors overflow-hidden group/badge",
  {
    variants: {
      variant: {
        success: "bg-emerald-soft text-emerald border-emerald-border",
        info: "bg-sky-soft text-sky border-sky-border",
        danger: "bg-rose-soft text-rose border-rose-border",
        beta: "bg-fuchsia-soft text-fuchsia border-fuchsia-border",
        neutral: "bg-neutral-soft text-neutral border-neutral-border",
        v2: "bg-pink-soft text-pink border-pink-border",
      },
      size: {
        sm: "h-4 px-1 py-0.5 gap-0.5 text-2xs rounded-sm [&>svg]:size-2.5!",
        base: "h-6 px-1.5 py-1 gap-1 text-xs rounded-md [&>svg]:size-3.5!",
      },
    },
    defaultVariants: {
      variant: "neutral",
      size: "base",
    },
  },
);

type TagVariant = VariantProps<typeof tagVariants>["variant"];

interface TagProps<ClassName extends string = string>
  extends
    Omit<useRender.ComponentProps<"span">, "className">,
    VariantProps<typeof tagVariants> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function Tag<ClassName extends string>({
  className,
  UNSAFE_className,
  variant = "neutral",
  size = "base",
  render,
  ...props
}: TagProps<ClassName>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(
          tagVariants({ variant, size }),
          className,
          UNSAFE_className,
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "tag",
      variant,
      size,
    },
  });
}

export { Tag, type TagVariant };
